import { useEffect, useRef, useCallback, useState } from "react";

const DB_NAME = "pixalloy_autosave";
const STORE_NAME = "snapshots";
const SAVE_INTERVAL = 60000; // 60 seconds

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE_NAME); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveSnapshot(eng) {
  if (!eng?.s) return;
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    // Serialize layer pixel data as blobs
    const layers = [];
    for (const l of eng.s.layers) {
      const entry = {
        name: l.name, op: l.op, blend: l.blend, vis: l.vis,
        type: l.type || "raster",
        adjType: l.adjType || null,
        adjParams: l.adjParams ? { ...l.adjParams } : null,
        fx: l.fx ? JSON.parse(JSON.stringify(l.fx)) : null,
      };
      // Only serialize pixel data for raster layers
      if (l.type !== "adjustment" && l.type !== "group") {
        const id = l.x.getImageData(0, 0, eng.s.w, eng.s.h);
        entry.pixels = id.data.buffer.slice(0); // copy ArrayBuffer
      }
      layers.push(entry);
    }
    const snapshot = {
      w: eng.s.w, h: eng.s.h,
      act: eng.s.act,
      layers,
      timestamp: Date.now(),
    };
    store.put(snapshot, "latest");
    db.close();
  } catch (err) {
    console.warn("[AutoSave] Failed:", err);
  }
}

async function loadSnapshot() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    return new Promise((resolve) => {
      const req = store.get("latest");
      req.onsuccess = () => { db.close(); resolve(req.result || null); };
      req.onerror = () => { db.close(); resolve(null); };
    });
  } catch { return null; }
}

async function clearSnapshot() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete("latest");
    db.close();
  } catch {}
}

/**
 * useAutoSave — periodic auto-save to IndexedDB.
 *
 * Returns:
 *   hasRecovery    boolean — true if unsaved work from previous session exists
 *   recoverData    object|null — the snapshot data
 *   doRecover      () => void — apply recovery (call eng.loadSnapshot externally)
 *   dismissRecover () => void — discard recovery data
 */
export function useAutoSave(eng, dirtyRef) {
  const [hasRecovery, setHasRecovery] = useState(false);
  const [recoverData, setRecoverData] = useState(null);
  const timerRef = useRef(null);

  // Check for recovery on mount
  useEffect(() => {
    loadSnapshot().then(snap => {
      if (snap && Date.now() - snap.timestamp < 86400000) { // within 24 hours
        setHasRecovery(true);
        setRecoverData(snap);
      }
    });
  }, []);

  // Periodic save
  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (dirtyRef.current && eng.current) {
        saveSnapshot(eng.current);
      }
    }, SAVE_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [eng, dirtyRef]);

  const dismissRecover = useCallback(() => {
    setHasRecovery(false);
    setRecoverData(null);
    clearSnapshot();
  }, []);

  const doRecover = useCallback(() => {
    setHasRecovery(false);
    // Recovery data is returned — caller applies it to engine
  }, []);

  return { hasRecovery, recoverData, doRecover, dismissRecover };
}
